import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';
import {
  MessageSquare,
  Send,
  X,
  MoreVertical,
  Trash2,
  CheckCircle2,
  Loader2,
  AtSign,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../../config';

const CommentSidebar = ({ documentId, isOpen, onClose, members = [] }) => {
  const { user, currentOrg } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef(null);
  const [showResolved, setShowResolved] = useState(false);

  const canEdit =
    currentOrg?.user_role && ['company', 'member'].includes(currentOrg.user_role);

  const fetchComments = useCallback(async () => {
    if (!documentId) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/documents/${documentId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    if (isOpen && documentId) {
      fetchComments();
    }
  }, [isOpen, documentId, fetchComments]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    // Extract mentions from comment text
    const mentionRegex = /@(\w+)/g;
    const mentionNames = [...newComment.matchAll(mentionRegex)].map(m => m[1]);
    const mentionIds = members
      .filter(m => mentionNames.some(name => 
        m.user_name.toLowerCase().includes(name.toLowerCase())
      ))
      .map(m => m.user_id);

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/documents/${documentId}/comments`, {
        content: newComment,
        mentions: mentionIds,
      });
      setComments(prev => [...prev, response.data]);
      setNewComment('');
      toast.success('Comment added');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to add comment';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolve = async (commentId) => {
    try {
      await axios.post(`${API_URL}/documents/${documentId}/comments/${commentId}/resolve`);
      setComments(prev => prev.map(c => 
        c.id === commentId ? { ...c, is_resolved: true } : c
      ));
      toast.success('Comment resolved');
    } catch (error) {
      toast.error('Failed to resolve comment');
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await axios.delete(`${API_URL}/documents/${documentId}/comments/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
      toast.success('Comment deleted');
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const insertMention = (member) => {
    const beforeCursor = newComment.substring(0, cursorPosition);
    const afterCursor = newComment.substring(cursorPosition);
    
    // Find the @ symbol position
    const atIndex = beforeCursor.lastIndexOf('@');
    if (atIndex === -1) return;
    
    const textBeforeAt = newComment.substring(0, atIndex);
    const newText = `${textBeforeAt}@${member.user_name} ${afterCursor}`;
    setNewComment(newText);
    setShowMentions(false);
    setMentionSearch('');
    
    // Focus back to textarea
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  const handleTextChange = (e) => {
    const value = e.target.value;
    const cursor = e.target.selectionStart;
    setNewComment(value);
    setCursorPosition(cursor);
    
    // Check for @ mentions
    const textBeforeCursor = value.substring(0, cursor);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    
    if (atIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(atIndex + 1);
      // Only show mentions if @ is at start or after a space, and no space after @
      if ((atIndex === 0 || textBeforeCursor[atIndex - 1] === ' ') && !textAfterAt.includes(' ')) {
        setShowMentions(true);
        setMentionSearch(textAfterAt.toLowerCase());
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  const filteredMembers = members.filter(m => 
    m.user_name.toLowerCase().includes(mentionSearch) ||
    m.user_email.toLowerCase().includes(mentionSearch)
  );

  const activeComments = comments.filter(c => !c.is_resolved);
  const resolvedComments = comments.filter(c => c.is_resolved);

  const renderCommentContent = (content) => {
    // Highlight @mentions in the content
    const parts = content.split(/(@\w+)/g);
    return parts.map((part, i) => {
      if (part.startsWith('@')) {
        return (
          <span key={i} className="text-blue-600 dark:text-blue-400 font-medium">
            {part}
          </span>
        );
      }
      return part;
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        'fixed right-0 top-0 h-full w-80 bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800',
        'shadow-lg z-50 flex flex-col transition-transform duration-300',
        isOpen ? 'translate-x-0' : 'translate-x-full'
      )}
      data-testid="comment-sidebar"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          <h3 className="font-semibold text-slate-900 dark:text-white">Comments</h3>
          {activeComments.length > 0 && (
            <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
              {activeComments.length}
            </Badge>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          data-testid="close-comments-button"
        >
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Comments List */}
      <ScrollArea className="flex-1 p-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : activeComments.length === 0 && !showResolved ? (
          <div className="text-center py-8">
            <MessageSquare className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-slate-500 dark:text-slate-400 text-sm">No comments yet</p>
            <p className="text-slate-400 dark:text-slate-500 text-xs mt-1">
              Start a discussion about this document
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {activeComments.map(comment => (
              <div
                key={comment.id}
                className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3"
                data-testid={`comment-${comment.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center">
                      <span className="text-xs font-medium text-white">
                        {comment.user_name?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">
                        {comment.user_name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {new Date(comment.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  {(comment.user_id === user?.id || canEdit) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-6 w-6">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!comment.is_resolved && (
                          <DropdownMenuItem onClick={() => handleResolve(comment.id)}>
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Resolve
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => handleDelete(comment.id)}
                          className="text-red-600 dark:text-red-400"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
                <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                  {renderCommentContent(comment.content)}
                </p>
              </div>
            ))}

            {/* Resolved Comments */}
            {resolvedComments.length > 0 && (
              <div className="mt-4">
                <button
                  className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 mb-2"
                  onClick={() => setShowResolved(!showResolved)}
                >
                  <ChevronRight className={cn('w-4 h-4 transition-transform', showResolved && 'rotate-90')} />
                  {resolvedComments.length} resolved comment{resolvedComments.length !== 1 ? 's' : ''}
                </button>
                {showResolved && (
                  <div className="space-y-3 pl-4 border-l-2 border-slate-200 dark:border-slate-700">
                    {resolvedComments.map(comment => (
                      <div
                        key={comment.id}
                        className="bg-slate-50/50 dark:bg-slate-800/30 rounded-lg p-3 opacity-70"
                        data-testid={`comment-resolved-${comment.id}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                          <span className="text-xs font-medium text-slate-500">Resolved</span>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          {renderCommentContent(comment.content)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </ScrollArea>

      {/* New Comment Input */}
      <div className="p-4 border-t border-slate-200 dark:border-slate-800">
        <form onSubmit={handleSubmitComment} className="space-y-2">
          <div className="relative">
            <Textarea
              ref={textareaRef}
              value={newComment}
              onChange={handleTextChange}
              placeholder="Write a comment... Use @ to mention"
              className="min-h-[80px] resize-none pr-10"
              data-testid="comment-input"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute bottom-2 right-2 h-8 w-8 text-slate-400 hover:text-slate-600"
              onClick={() => {
                setNewComment(prev => prev + '@');
                setShowMentions(true);
                textareaRef.current?.focus();
              }}
              title="Mention someone"
            >
              <AtSign className="w-4 h-4" />
            </Button>
            
            {/* Mention Dropdown */}
            {showMentions && filteredMembers.length > 0 && (
              <div className="absolute bottom-full left-0 w-full mb-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-40 overflow-auto z-10">
                {filteredMembers.map(member => (
                  <button
                    key={member.user_id}
                    type="button"
                    className="w-full px-3 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                    onClick={() => insertMention(member)}
                  >
                    <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
                      <span className="text-xs font-medium text-white">
                        {member.user_name?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                        {member.user_name}
                      </p>
                      <p className="text-xs text-slate-500 truncate">{member.user_email}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <Button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            disabled={submitting || !newComment.trim()}
            data-testid="submit-comment-button"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Posting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Post Comment
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CommentSidebar;
